import { Button } from "@/components/ui/button";
import { Cloud, Compass, Utensils, Loader2, Car } from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "@/hooks/use-analytics";
import { motion } from "framer-motion";
import { useIsMobile } from "@/hooks/use-mobile";

interface TripCardActionsProps {
  onWeatherClick: (e: React.MouseEvent) => void;
  onActivitiesClick: (e: React.MouseEvent) => void;
  onFoodClick: (e: React.MouseEvent) => void;
  onTransferClick: (e: React.MouseEvent) => void;
  handleButtonClick: (e: React.MouseEvent) => void;
  tripId: string;
  destinationName: string;
  layout?: "horizontal" | "vertical";
}

const TripCardActions = ({
  onWeatherClick,
  onActivitiesClick,
  onFoodClick,
  onTransferClick,
  handleButtonClick,
  tripId,
  destinationName,
  layout = "horizontal"
}: TripCardActionsProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { trackTripCardAction } = useAnalytics();
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<{
    weather: boolean;
    activities: boolean;
    food: boolean;
    transfer: boolean;
  }>({
    weather: false,
    activities: false,
    food: false,
    transfer: false
  });

  const handleWeatherClick = (e: React.MouseEvent) => {
    e?.stopPropagation(); // Stop event propagation
    setIsLoading(prev => ({ ...prev, weather: true }));
    trackTripCardAction("weather", tripId, destinationName);
    onWeatherClick(e);
    // Reset loading state after a reasonable timeout if API doesn't respond
    setTimeout(() => {
      setIsLoading(prev => ({ ...prev, weather: false }));
    }, 5000);
  };

  const handleActivitiesClick = (e: React.MouseEvent) => {
    e?.stopPropagation(); // Stop event propagation
    setIsLoading(prev => ({ ...prev, activities: true }));
    trackTripCardAction("activities", tripId, destinationName);
    onActivitiesClick(e);
    // Reset loading state after a reasonable timeout if API doesn't respond
    setTimeout(() => {
      setIsLoading(prev => ({ ...prev, activities: false }));
    }, 5000);
  };

  const handleFoodClick = (e: React.MouseEvent) => {
    e?.stopPropagation(); // Stop event propagation
    setIsLoading(prev => ({ ...prev, food: true }));
    trackTripCardAction("food", tripId, destinationName);
    onFoodClick(e);
    // Reset loading state after a reasonable timeout if API doesn't respond
    setTimeout(() => {
      setIsLoading(prev => ({ ...prev, food: false }));
    }, 5000);
  };

  const handleTransferClick = (e: React.MouseEvent) => {
    e?.stopPropagation(); // Stop event propagation
    setIsLoading(prev => ({ ...prev, transfer: true }));
    trackTripCardAction("taxi", tripId, destinationName);
    onTransferClick(e);
    // Reset loading state after a reasonable timeout if API doesn't respond
    setTimeout(() => {
      setIsLoading(prev => ({ ...prev, transfer: false }));
    }, 5000);
  };

  const DesktopButton = ({ 
    id, 
    icon, 
    text, 
    onClick, 
    isLoading 
  }: { 
    id: string; 
    icon: React.ReactNode; 
    text: string; 
    onClick: (e: React.MouseEvent) => void; 
    isLoading: boolean; 
  }) => {
    return (
      <motion.div
        className="relative"
        onMouseEnter={() => setHoveredButton(id)}
        onMouseLeave={() => setHoveredButton(null)}
        onClick={(e) => e.stopPropagation()} // Stop event propagation at the container level
      >
        <Button
          variant="outline"
          size="sm"
          className={layout === "vertical" 
            ? "flex items-center gap-1 text-xs py-2 h-10 justify-center mb-2 overflow-hidden" 
            : "flex items-center gap-1 text-xs py-1 h-8 justify-center overflow-hidden"
          }
          style={{ width: hoveredButton === id ? "auto" : "40px" }}
          onClick={onClick}
          disabled={isLoading}
          onMouseDown={(e) => e.stopPropagation()} 
          onTouchStart={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          onKeyPress={(e) => e.stopPropagation()}
        >
          <span className="flex items-center justify-center">
            {isLoading ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              icon
            )}
          </span>
          
          {hoveredButton === id && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              transition={{ duration: 0.2 }}
              className="ml-1 whitespace-nowrap"
            >
              {text}
            </motion.span>
          )}
        </Button>
      </motion.div>
    );
  };

  const MobileButton = ({ 
    icon, 
    text, 
    onClick, 
    isLoading 
  }: { 
    icon: React.ReactNode; 
    text: string; 
    onClick: (e: React.MouseEvent) => void; 
    isLoading: boolean; 
  }) => {
    const buttonClasses = layout === "vertical" 
      ? "flex items-center gap-1 text-xs py-2 h-10 w-36 justify-center mb-2" 
      : "flex items-center gap-1 text-xs py-1 h-8 w-full justify-start";

    return (
      <div onClick={(e) => e.stopPropagation()}>
        <Button 
          variant="outline" 
          size="sm" 
          className={buttonClasses}
          onClick={onClick}
          disabled={isLoading}
          onMouseDown={(e) => e.stopPropagation()} 
          onTouchStart={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          onKeyPress={(e) => e.stopPropagation()}
        >
          {isLoading ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            icon
          )}
          {text}
        </Button>
      </div>
    );
  };

  return (
    <div 
      className={layout === "vertical" ? "flex flex-col items-center w-[70px]" : "flex flex-col gap-2"}
      onClick={(e) => e.stopPropagation()} // Stop event propagation at the container level
      onMouseDown={(e) => e.stopPropagation()} 
      onTouchStart={(e) => e.stopPropagation()}
    >
      {isMobile ? (
        <>
          <MobileButton 
            icon={<Cloud className="w-3 h-3" />}
            text={t("tripCard.weather")}
            onClick={handleWeatherClick}
            isLoading={isLoading.weather}
          />
          
          <MobileButton 
            icon={<Compass className="w-3 h-3" />}
            text={t("tripCard.activities")}
            onClick={handleActivitiesClick}
            isLoading={isLoading.activities}
          />

          <MobileButton 
            icon={<Utensils className="w-3 h-3" />}
            text={t("tripCard.food")}
            onClick={handleFoodClick}
            isLoading={isLoading.food}
          />

          <MobileButton 
            icon={<Car className="w-3 h-3" />}
            text={t("tripCard.taxiTransfer")}
            onClick={handleTransferClick}
            isLoading={isLoading.transfer}
          />
        </>
      ) : (
        <>
          <DesktopButton 
            id="weather"
            icon={<Cloud className="w-3 h-3" />}
            text={t("tripCard.weather")}
            onClick={handleWeatherClick}
            isLoading={isLoading.weather}
          />
          
          <DesktopButton 
            id="activities"
            icon={<Compass className="w-3 h-3" />}
            text={t("tripCard.activities")}
            onClick={handleActivitiesClick}
            isLoading={isLoading.activities}
          />

          <DesktopButton 
            id="food"
            icon={<Utensils className="w-3 h-3" />}
            text={t("tripCard.food")}
            onClick={handleFoodClick}
            isLoading={isLoading.food}
          />

          <DesktopButton 
            id="transfer"
            icon={<Car className="w-3 h-3" />}
            text={t("tripCard.taxiTransfer")}
            onClick={handleTransferClick}
            isLoading={isLoading.transfer}
          />
        </>
      )}
    </div>
  );
};

export default TripCardActions;
